import { __decorate } from "tslib";
import __NG_CLI_RESOURCE__0 from "./dayparts.component.html?ngResource";
import { Component, Input, Output, ViewChildren, EventEmitter, } from '@angular/core';
import _ from 'lodash';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { HOURS_PER_DAY } from 'Utils/time';
const HALF_DAY = HOURS_PER_DAY / 2;
import { CommonModule } from '@angular/common';
let DaypartsComponent = class DaypartsComponent {
    options = {};
    model;
    calendarChange = new EventEmitter();
    tds;
    days = generateDays();
    hours = generateHours();
    columns = [];
    rows = [];
    isDragging = false;
    static selectedClass = 'selected';
    startCell = null;
    endCell = null;
    selected = new Map();
    isSelecting = false;
    #destroy$ = new Subject();
    ngOnInit() {
        if (this.options.twelveOClockLabel) {
            const labels = { 0: 'midnight', 12: 'noon' };
            for (const hr of _.keys(labels)) {
                const hour = this.hours.get(hr);
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                hour.title = `(${labels[hr]}) ${hour.title}`;
            }
        }
        this.setupRowsAndColumns();
        this.parsePreSelected();
    }
    ngOnChanges(changes) {
        if (changes.model && !changes.model.firstChange) {
            this.clearElements();
            this.parsePreSelected();
        }
        if (changes.options && !changes.options.firstChange &&
            changes.options.currentValue.reverse !== changes.options.previousValue.reverse) {
            this.handleReverseChange();
        }
    }
    ngOnDestroy() {
        this.#destroy$.next(null);
        this.#destroy$.complete();
    }
    setupRowsAndColumns() {
        if (this.options.reverse) {
            this.rows = [...this.days.values()];
            this.columns = [...this.hours.values()];
        }
        else {
            this.rows = [...this.hours.values()];
            this.columns = [...this.days.values()];
        }
    }
    handleReverseChange() {
        this.tds.changes
            .pipe(take(1))
            .pipe(takeUntil(this.#destroy$))
            .subscribe(() => {
            this.transposeSelected();
        });
        this.setupRowsAndColumns();
    }
    clearElements() {
        this.selected.forEach((_val, key) => {
            const [col, row] = key.split('.');
            this.deselectCell(this.rows[row].index, this.columns[col].index);
        });
    }
    transposeSelected() {
        const temp = [...this.selected.keys()];
        this.selected.clear();
        temp.forEach((key) => {
            const [col, row] = key.split('.');
            // inverse
            this.selectCell(Number(col), Number(row));
        });
    }
    parsePreSelected() {
        if (!_.isEmpty(this.model)) {
            _.forOwn(this.model, (hours, dayKey) => {
                const day = this.days.get(dayKey);
                if (!day) {
                    return;
                }
                _.forOwn(hours, (enabled, hourKey) => {
                    if (enabled) {
                        const hour = this.hours.get(hourKey);
                        if (!hour) {
                            return;
                        }
                        if (this.options.reverse) {
                            this.selectCell(day.index, hour.index);
                        }
                        else {
                            this.selectCell(hour.index, day.index);
                        }
                    }
                });
            });
        }
    }
    parseSelectedCells() {
        const ics = '';
        const schedule = {};
        this.selected.forEach((_val, key) => {
            const [col, row] = key.split('.');
            const { day, hour } = _.keyBy([this.columns[col], this.rows[row]], 'type');
            _.setWith(schedule, [day.key, hour.key], true, Object);
        });
        return { ics, schedule };
    }
    notifyCalendarChange() {
        this.calendarChange.emit(this.parseSelectedCells());
    }
    selectRow(row) {
        const cellsToSet = [];
        let shouldSelect = false;
        for (const col of this.columns) {
            cellsToSet.push([row.index, col.index]);
            if (!this.getCell(row, col)) {
                shouldSelect = true;
            }
        }
        cellsToSet.forEach(cell => this[shouldSelect ? 'selectCell' : 'deselectCell'](...cell));
        this.notifyCalendarChange();
    }
    selectCol(col) {
        const cellsToSet = [];
        let shouldSelect = false;
        for (const row of this.rows) {
            cellsToSet.push([row.index, col.index]);
            if (!this.getCell(row, col)) {
                shouldSelect = true;
            }
        }
        cellsToSet.forEach(cell => this[shouldSelect ? 'selectCell' : 'deselectCell'](...cell));
        this.notifyCalendarChange();
    }
    mousePressed($event, row, column) {
        if ($event.buttons === 1 /* Buttons.Primary */) {
            this.isDragging = true;
            this.setStartCell(row, column);
            this.setEndCell(row, column);
        }
    }
    mouseReleased(row, column) {
        if (!this.isDragging) {
            return;
        }
        this.setEndCell(row, column);
        this.endDragging();
    }
    mouseEntered(row, column) {
        if (!this.isDragging) {
            return;
        }
        this.setEndCell(row, column);
    }
    endDragging() {
        if (!(this.isDragging && this.startCell && this.endCell)) {
            return;
        }
        const topLeft = {
            column: Math.min(this.startCell.column.index, this.endCell.column.index),
            row: Math.min(this.startCell.row.index, this.endCell.row.index),
        };
        const bottomRight = {
            column: Math.max(this.startCell.column.index, this.endCell.column.index),
            row: Math.max(this.startCell.row.index, this.endCell.row.index),
        };
        for (let i = topLeft.row; i <= bottomRight.row; i++) {
            for (let j = topLeft.column; j <= bottomRight.column; j++) {
                if (this.isSelecting) {
                    this.selectCell(i, j);
                }
                else {
                    this.deselectCell(i, j);
                }
            }
        }
        this.isDragging = false;
        this.startCell = null;
        this.endCell = null;
        this.notifyCalendarChange();
    }
    showDragClass(row, col) {
        if (!(this.isDragging && this.startCell && this.endCell)) {
            return '';
        }
        const { endCell: { column: { index: endCol }, row: { index: endRow } }, startCell: { column: { index: startCol }, row: { index: startRow } }, } = this;
        const rowDragDirection = startRow <= endRow ? [startRow, endRow] : [endRow, startRow];
        const colDragDirection = startCol <= endCol ? [startCol, endCol] : [endCol, startCol];
        // +1 to bypass range exclusion
        rowDragDirection[1] += 1;
        colDragDirection[1] += 1;
        const cellIncluded = _.inRange(row.index, ...rowDragDirection) && _.inRange(col.index, ...colDragDirection);
        return cellIncluded ?
            this.isSelecting ? 'drag-off' : 'drag-on' :
            '';
    }
    setStartCell(row, column) {
        const key = `${column.index}.${row.index}`;
        this.isSelecting = !this.selected.has(key);
        this.startCell = { row, column };
    }
    setEndCell(row, column) {
        if (!this.startCell) {
            return;
        }
        this.endCell = { row, column };
    }
    getCell(row, col) {
        return this.selected.get(`${col.index}.${row.index}`);
    }
    selectCell(row, col) {
        const key = `${col}.${row}`;
        this.selected.set(key, true);
    }
    deselectCell(row, col) {
        const key = `${col}.${row}`;
        this.selected.delete(key);
    }
    clear() {
        this.clearElements();
        this.notifyCalendarChange();
    }
    static propDecorators = {
        options: [{ type: Input }],
        model: [{ type: Input }],
        calendarChange: [{ type: Output }],
        tds: [{ type: ViewChildren, args: ['spot',] }]
    };
};
DaypartsComponent = __decorate([
    Component({
        selector: 'dayparts',
        template: __NG_CLI_RESOURCE__0,
        standalone: true,
        imports: [CommonModule]
    })
], DaypartsComponent);
export { DaypartsComponent };
function generateDays() {
    return _.reduce(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'], (acc, title, index) => {
        const key = `${(index + 1) % 7}`;
        return acc.set(key, { title, index, key, type: 'day' });
    }, new Map());
}
function generateHours() {
    return new Map(_.times(HOURS_PER_DAY * 2, (hr) => {
        const hour = Math.floor(hr / 2);
        const half = hr % 2;
        const title = `${hour % HALF_DAY || '12'}:${half ? '30' : '00'} ${hour < HALF_DAY ? 'AM' : 'PM'}`;
        const key = `${hour}${half ? '_5' : ''}`;
        return [key, { title, index: hr, key, type: 'hour' }];
    }));
}
